import { useMemo } from 'react';
import axios from 'axios';
import { useQuery, useQueryClient, useMutation, keepPreviousData } from '@tanstack/react-query';
import type { EmployeeLookup, InvitationUserLookupResponse } from './types';
import { toast } from 'sonner';
import { getApiErrorMessage, getApiErrorStatus } from '@/shared/api/getApiErrorMessage';
import { explainBillingRefusal } from '@/shared/api/billingToast';
import { parseValidationErrors } from '@/shared/api/errorHandling';
import { billingKeys } from '@/entities/billing/model/keys';
import { shiftKeys } from '@/entities/shift/model/queries';
import { attendanceKeys } from '@/entities/attendance/model/queries';
import { guardLocationKeys } from '@/entities/guard-location/model/queries';
import {
  fetchEmployees,
  fetchEmployeeLookup,
  fetchGuardLookup,
  fetchEmployeeProfile,
  fetchCurrentEmployeeProfile,
  createEmployee,
  updateCurrentProfile,
  updateEmployee,
  updateCurrentUser,
  deleteCurrentEmployeeCredential,
  activateEmployee,
  deactivateEmployee,
  withdrawEmployee,
  endEmployment,
  fetchEndPreflight,
  editEmploymentEnd,
  cancelEmploymentEnd,
  suspendEmployee,
  liftEmployeeSuspension,
  fetchExitSummary,
  tickExitChecklistItem,
  fetchEmploymentHistory,
  rehireEmployees,
  recordExitDetails,
  sendInvitation,
  lookupInvitationUserByEmail,
  getAccountInvitations,
  resendInvitation,
  cancelInvitation,
  updateInvitationRole,
  bulkAssignDepartment,
  bulkAssignEmploymentCategory,
  changeEmployeeType,
} from '../api/employeeApi';
import type {
  CreateEmployeeRequest,
  EmployeeFilters,
  UpdateProfileRequest,
  UpdateEmployeeRequest,
  UpdateUserRequest,
  MyProfileResponse,
  SendInvitationRequest,
  GetAccountInvitationsRequest,
  UpdateInvitationRoleRequest,
  BulkAssignDepartmentRequest,
  BulkAssignEmploymentCategoryRequest,
  ChangeEmployeeTypeRequest,
  EndEmploymentRequest,
  EditEndRequest,
  EndPreflightParams,
  ExitSummary,
  TickExitChecklistItemRequest,
  RehireRequest,
  RecordExitDetailsRequest,
} from './types';
import { useLookup } from '@/shared/hooks/useLookup';
import { lookupKeys } from '@/shared/queries/lookupKeys';

export const employeeKeys = {
  all: ['employees'] as const,
  list: (filters: EmployeeFilters) => [...employeeKeys.all, 'list', filters] as const,
  profile: (id: number) => [...employeeKeys.all, 'profile', id] as const,
  currentUser: () => [...employeeKeys.all, 'me'] as const,
  exitSummary: (id: number) => [...employeeKeys.all, 'exit-summary', id] as const,
  history: (id: number) => [...employeeKeys.all, 'employment-history', id] as const,
};

export const useEmployees = (filters: EmployeeFilters) => {
  return useQuery({
    queryKey: ['employees', filters],
    queryFn: () => fetchEmployees(filters),
  });
};

export const useEmployeeLookup = (employeeId: number | null) =>
  useLookup({
    queryKey: lookupKeys.employee.single(employeeId!),
    queryFn: async () => {
      const results = await fetchEmployeeLookup([employeeId!]);
      return results[0] ?? null;
    },
    tier: 'standard',
    enabled: employeeId != null,
  });

export const useEmployeesLookup = (ids: number[]) =>
  useLookup<EmployeeLookup[], EmployeeLookup>({
    queryKey: lookupKeys.employee.batch(ids),
    queryFn: () => fetchEmployeeLookup(ids),
    tier: 'standard',
    enabled: ids.length > 0,
    seed: {
      items: (data) => data,
      key: (employee) => lookupKeys.employee.single(employee.id),
    },
  });

export const useBackofficeLookup = () =>
  useLookup({
    queryKey: lookupKeys.employee.backoffice,
    queryFn: () => fetchEmployeeLookup(),
    tier: 'standard',
  });

export const useGuardLookup = (clientId?: number) =>
  useLookup({
    queryKey: clientId != null ? lookupKeys.guard.byClient(clientId) : lookupKeys.guard.list,
    queryFn: () => fetchGuardLookup(undefined, clientId),
    // Volatile either way: this list decides who can be assigned, and employment
    // status changes while a scheduler has the panel open.
    tier: 'volatile',
  });

export const useGuardHistoryLookup = (clientId?: number) => {
  const active = useGuardLookup(clientId);
  const everyone = useLookup({
    queryKey: lookupKeys.guard.withEnded(clientId),
    queryFn: () => fetchGuardLookup(undefined, clientId, { includeEnded: true }),
    tier: 'volatile',
  });

  const data = useMemo(() => {
    if (!active.data) return undefined;
    const activeIds = new Set(active.data.map((g) => g.id));
    const ended = (everyone.data ?? [])
      .filter((g) => !activeIds.has(g.id))
      .sort((a, b) => `${a.firstName} ${a.lastName}`.localeCompare(`${b.firstName} ${b.lastName}`));
    return { active: active.data, ended };
  }, [active.data, everyone.data]);

  return { data, isLoading: active.isLoading };
};

export const useGuardsLookup = (ids: number[], options?: { skipErrorToast?: boolean; enabled?: boolean }) =>
  useLookup({
    queryKey: lookupKeys.guard.batch(ids),
    queryFn: () => fetchGuardLookup(ids, undefined, options),
    tier: 'standard',
    enabled: ids.length > 0 && (options?.enabled ?? true),
    // A draft change to the week's guard set changes this batch's ids and
    // refetches; without this every card falls back to "Guard #id" until it
    // returns, even though the previous names are still perfectly good.
    placeholderData: keepPreviousData,
  });

/**
 * Resolves a set of user ids to names across both back-office staff and guards,
 * since an actor (e.g. an audit-log entry) may be either.
 */
export const usePeopleLookup = (ids: number[]) => {
  const backoffice = useEmployeesLookup(ids);
  const guards = useGuardsLookup(ids);

  const data = useMemo(() => {
    const byId = new Map<number, EmployeeLookup>();
    for (const person of backoffice.data ?? []) byId.set(person.id, person);
    for (const person of guards.data ?? []) byId.set(person.id, person);
    return [...byId.values()];
  }, [backoffice.data, guards.data]);

  return {
    data,
    isLoading: backoffice.isLoading || guards.isLoading,
    isError: backoffice.isError || guards.isError,
    isFetching: backoffice.isFetching || guards.isFetching,
  };
};

export const useEmployeeProfile = (employeeId: number, options?: { enabled?: boolean }) => {
  return useQuery({
    queryKey: employeeKeys.profile(employeeId),
    queryFn: () => fetchEmployeeProfile(employeeId),
    staleTime: 2 * 60 * 1000, // 2 minutes
    gcTime: 5 * 60 * 1000, // 5 minutes
    enabled: options?.enabled,
  });
};

export const useCurrentUserProfile = (options?: { enabled?: boolean }) => {
  return useQuery<MyProfileResponse>({
    queryKey: employeeKeys.currentUser(),
    queryFn: fetchCurrentEmployeeProfile,
    staleTime: 4 * 60 * 60 * 1000, // 4 hours
    enabled: options?.enabled,
  });
};

// Mutation for creating a new employee

export const useCreateEmployee = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (data: CreateEmployeeRequest) => createEmployee(data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: employeeKeys.all });
      toast.success('Employee created successfully');
    },
  });
};

// Mutations for updating employee data

export const useUpdateCurrentProfile = (employeeId: number) => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (data: UpdateProfileRequest) => updateCurrentProfile(data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: employeeKeys.profile(employeeId) });
      queryClient.invalidateQueries({ queryKey: employeeKeys.currentUser() });
      queryClient.invalidateQueries({ queryKey: employeeKeys.all });
    },
  });
};

export const useUpdateEmployee = (employeeId: number) => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (data: UpdateEmployeeRequest) => updateEmployee(employeeId, data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: employeeKeys.profile(employeeId) });
      queryClient.invalidateQueries({ queryKey: employeeKeys.currentUser() });
      queryClient.invalidateQueries({ queryKey: employeeKeys.all });
    },
  });
};

export const useUpdateCurrentUser = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (data: UpdateUserRequest) => updateCurrentUser(data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: employeeKeys.currentUser() });
      queryClient.invalidateQueries({ queryKey: employeeKeys.all });
    },
  });
};

interface ActivateEmployeeOptions {
  /**
   * A chance to handle a capacity refusal before the generic billing toast does.
   *
   * Returns true when it took the error. Supplied by the calling feature rather than wired in here
   * — under this model the answer to "your plan is full" is usually "buy one more", which is a
   * dialogue, and a hook in the entities layer has no business rendering one. See
   * `useBuySeatsOnRefusal`, which the employee header passes in.
   */
  onCapacityRefusal?: (error: unknown) => boolean;
}

export const useActivateEmployee = (employeeId: number, options: ActivateEmployeeOptions = {}) => {
  const queryClient = useQueryClient();
  const { onCapacityRefusal } = options;

  return useMutation({
    mutationFn: () => activateEmployee(employeeId),
    onSuccess: (result) => {
      queryClient.invalidateQueries({ queryKey: employeeKeys.profile(employeeId) });
      queryClient.invalidateQueries({ queryKey: employeeKeys.all });
      queryClient.invalidateQueries({ queryKey: billingKeys.all });
      const warnings = (result?.warnings ?? []).map((w) => w.message).filter(Boolean);
      toast.success('Employee activated successfully', warnings.length > 0 ? { description: warnings.join(' ') } : undefined);
    },
    onError: async (error) => {
      // First refusal of the plan's user limit that the caller can answer in place: it opens the
      // buy-more dialogue and retries this activation afterwards. It declines anything it cannot
      // answer, and then the toast below explains it as it always has.
      if (onCapacityRefusal?.(error)) return;

      // 402: the plan's user limit. 403 past-due/suspended: the account's billing state.
      if (explainBillingRefusal(error)) return;

      // 422 means the server disagrees with what we showed: most often the employee is already active
      // (another tab, another admin). Refresh so the page tells the truth. Unlike a site, which
      // answers 204 for an already-active one, an employee answers 422.
      if (getApiErrorStatus(error) === 422) {
        queryClient.invalidateQueries({ queryKey: employeeKeys.profile(employeeId) });
        queryClient.invalidateQueries({ queryKey: employeeKeys.all });
        const data = axios.isAxiosError(error) ? error.response?.data : undefined;
        const alreadyActive = parseValidationErrors(data).some((p) => p.propertyName === 'IsActive');
        if (alreadyActive) {
          toast.info('This user is already active.');
          return;
        }
      }
      toast.error(await getApiErrorMessage(error));
    },
  });
};

export const useDeactivateEmployee = (employeeId: number) => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (data: { inactiveReason: string }) => deactivateEmployee(employeeId, data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: employeeKeys.profile(employeeId) });
      queryClient.invalidateQueries({ queryKey: employeeKeys.all });
      toast.success('Employee deactivated successfully');
    },
  });
};

const useRefreshEmployeeState = () => {
  const queryClient = useQueryClient();
  return () => {
    queryClient.invalidateQueries({ queryKey: employeeKeys.all });
    queryClient.invalidateQueries({ queryKey: billingKeys.all });
    queryClient.invalidateQueries({ queryKey: shiftKeys.all });
    queryClient.invalidateQueries({ queryKey: attendanceKeys.all });
    queryClient.invalidateQueries({ queryKey: guardLocationKeys.live });
  };
};

const isStateConflict = (error: unknown) => {
  const status = getApiErrorStatus(error);
  return status === 409 || status === 422;
};

export const useEndPreflight = (params: EndPreflightParams, enabled: boolean) =>
  useQuery({
    queryKey: [
      ...employeeKeys.all,
      'end-preflight',
      [...params.ids].sort((a, b) => a - b),
      params.lastDay,
      params.endAccessNow,
      params.lastDayFromAttendance ?? false,
    ],
    queryFn: () => fetchEndPreflight(params),
    enabled: enabled && params.ids.length > 0 && params.lastDay !== '',
    staleTime: 0,
    gcTime: 0,
    retry: false,
    placeholderData: keepPreviousData,
  });

export const useEndEmployment = () => {
  const refresh = useRefreshEmployeeState();
  return useMutation({
    mutationFn: (data: EndEmploymentRequest) => endEmployment(data),
    onSuccess: refresh,
    onError: (error) => {
      if (isStateConflict(error)) refresh();
    },
  });
};

export const useEditEmploymentEnd = (employeeId: number) => {
  const refresh = useRefreshEmployeeState();
  return useMutation({
    mutationFn: (data: EditEndRequest) => editEmploymentEnd(employeeId, data),
    onSuccess: refresh,
    onError: (error) => {
      if (isStateConflict(error)) refresh();
    },
  });
};

export const useCancelEmploymentEnd = (employeeId: number) => {
  const refresh = useRefreshEmployeeState();
  return useMutation({
    mutationFn: () => cancelEmploymentEnd(employeeId),
    onSuccess: refresh,
    onError: (error) => {
      if (isStateConflict(error)) refresh();
    },
  });
};

export const useSuspendEmployee = (employeeId: number) => {
  const refresh = useRefreshEmployeeState();
  return useMutation({
    mutationFn: (data: { note: string }) => suspendEmployee(employeeId, data),
    onSuccess: refresh,
    onError: (error) => {
      if (isStateConflict(error)) refresh();
    },
  });
};

export const useLiftSuspension = (employeeId: number) => {
  const refresh = useRefreshEmployeeState();
  return useMutation({
    mutationFn: () => liftEmployeeSuspension(employeeId),
    onSuccess: () => {
      refresh();
      toast.success('Suspension lifted');
    },
    onError: async (error) => {
      if (isStateConflict(error)) refresh();
      toast.error(await getApiErrorMessage(error));
    },
  });
};

export const useWithdrawEmployee = (employeeId: number) => {
  const refresh = useRefreshEmployeeState();
  return useMutation({
    mutationFn: (data: { reason: string }) => withdrawEmployee(employeeId, data),
    onSuccess: () => {
      refresh();
      toast.success('Hire withdrawn');
    },
    onError: (error) => {
      if (isStateConflict(error)) refresh();
    },
  });
};

export const useExitSummary = (employeeId: number, enabled: boolean) =>
  useQuery({
    queryKey: employeeKeys.exitSummary(employeeId),
    queryFn: () => fetchExitSummary(employeeId),
    enabled: enabled && employeeId > 0,
    retry: false,
  });

export const useEmploymentHistory = (employeeId: number, enabled: boolean) =>
  useQuery({
    queryKey: employeeKeys.history(employeeId),
    queryFn: () => fetchEmploymentHistory(employeeId),
    enabled: enabled && employeeId > 0,
    retry: false,
  });

export const useTickExitChecklistItem = (employeeId: number) => {
  const queryClient = useQueryClient();
  const key = employeeKeys.exitSummary(employeeId);
  return useMutation({
    mutationFn: ({ itemKey, data }: { itemKey: string; data: TickExitChecklistItemRequest }) =>
      tickExitChecklistItem(employeeId, itemKey, data),
    onMutate: async ({ itemKey, data }) => {
      await queryClient.cancelQueries({ queryKey: key });
      const previous = queryClient.getQueryData<ExitSummary>(key);
      if (previous) {
        queryClient.setQueryData<ExitSummary>(key, {
          ...previous,
          items: (previous.items ?? []).map((item) =>
            item.key === itemKey
              ? { ...item, done: data.done, filedOn: data.done ? (data.filedOn ?? null) : null, note: data.note ?? null }
              : item,
          ),
        });
      }
      return { previous };
    },
    onError: (_error, _vars, context) => {
      if (context?.previous) queryClient.setQueryData(key, context.previous);
    },
    onSettled: () => {
      queryClient.invalidateQueries({ queryKey: key });
      queryClient.invalidateQueries({
        predicate: (q) => q.queryKey[0] === employeeKeys.all[0] && typeof q.queryKey[1] === 'object',
      });
    },
  });
};

export const useRehireEmployees = () => {
  const refresh = useRefreshEmployeeState();
  return useMutation({
    mutationFn: (data: RehireRequest) => rehireEmployees(data),
    onSuccess: refresh,
    onError: (error) => {
      if (isStateConflict(error)) refresh();
    },
  });
};

export const useRecordExitDetails = (employeeId: number) => {
  const refresh = useRefreshEmployeeState();
  return useMutation({
    mutationFn: (data: RecordExitDetailsRequest) => recordExitDetails(employeeId, data),
    onSuccess: refresh,
    onError: (error) => {
      if (isStateConflict(error)) refresh();
    },
  });
};

// ========================================
// Employee Credentials Mutations
// ========================================

export const useEmployeeCredentials = (employeeId: number, isCurrentUser = false) => {
  return useQuery({
    queryKey: [...employeeKeys.profile(employeeId), 'credentials'],
    queryFn: () =>
      import('../api/employeeApi').then(api =>
        isCurrentUser
          ? api.fetchCurrentEmployeeCredentials()
          : api.fetchEmployeeCredentials(employeeId)
      ),
  });
};

export const useCreateCredential = (employeeId: number, isCurrentUser = false) => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (data: import('./types').CreateCredentialRequest) =>
      import('../api/employeeApi').then(api =>
        isCurrentUser
          ? api.createCurrentEmployeeCredential(data)
          : api.createEmployeeCredential(employeeId, data)
      ),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: [...employeeKeys.profile(employeeId), 'credentials'] });
      toast.success('Credential added successfully');
    },
  });
};

export const useVerifyCredential = (employeeId: number) => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ id, data }: { id: number; data: import('./types').VerifyCredentialRequest }) =>
      import('../api/employeeApi').then(api => api.verifyEmployeeCredential(id, data)),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: [...employeeKeys.profile(employeeId), 'credentials'] });
      toast.success('Credential verified');
    },
  });
};

export const useRejectCredential = (employeeId: number) => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ id, data }: { id: number; data: import('./types').RejectCredentialRequest }) =>
      import('../api/employeeApi').then(api => api.rejectEmployeeCredential(id, data)),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: [...employeeKeys.profile(employeeId), 'credentials'] });
      toast.success('Credential rejected');
    },
  });
};

export const useDeleteCredential = (employeeId: number) => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (credentialId: number) => deleteCurrentEmployeeCredential(credentialId),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: [...employeeKeys.profile(employeeId), 'credentials'] });
      toast.success('Credential deleted');
    },
  });
};

// ========================================
// Employment Invitations Mutations
// ========================================

export const invitationKeys = {
  all: ['account-invitations'] as const,
  list: (params: GetAccountInvitationsRequest) =>
    [...invitationKeys.all, 'list', params] as const,
};

export const useSendInvitation = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (data: SendInvitationRequest) => sendInvitation(data),
    onSuccess: () => {
      toast.success('Invitation sent successfully');
      queryClient.invalidateQueries({ queryKey: invitationKeys.all });
    },
  });
};

export const useInvitationUserLookup = (email: string, enabled: boolean) => {
  return useQuery<InvitationUserLookupResponse | null>({
    queryKey: ['invitation-user-lookup', email.toLowerCase()],
    queryFn: () => lookupInvitationUserByEmail(email),
    enabled: enabled && email.length > 0,
    staleTime: 30_000,
    retry: false,
  });
};

export const useAccountInvitations = (params: GetAccountInvitationsRequest) => {
  return useQuery({
    queryKey: invitationKeys.list(params),
    queryFn: () => getAccountInvitations(params),
    staleTime: 15_000,
  });
};

export const useResendInvitation = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (id: number) => resendInvitation(id),
    onSuccess: () => {
      toast.success('Invitation resent');
      queryClient.invalidateQueries({ queryKey: invitationKeys.all });
    },
  });
};

export const useCancelInvitation = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (id: number) => cancelInvitation(id),
    onSuccess: () => {
      toast.success('Invitation revoked');
      queryClient.invalidateQueries({ queryKey: invitationKeys.all });
    },
  });
};

export const useUpdateInvitationRole = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (args: { id: number; data: UpdateInvitationRoleRequest }) =>
      updateInvitationRole(args.id, args.data),
    onSuccess: () => {
      toast.success('Role updated');
      queryClient.invalidateQueries({ queryKey: invitationKeys.all });
    },
  });
};

export const useBulkAssignDepartment = () => {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: (data: BulkAssignDepartmentRequest) => bulkAssignDepartment(data),
    onSuccess: (result) => {
      queryClient.invalidateQueries({ queryKey: ['employees'] })
      const success = result.updatedCount
      const failed = result.failures?.length ?? 0
      if (failed > 0) {
        toast.warning(`Updated ${success} employees; ${failed} failed`)
      } else {
        toast.success(`Updated ${success} ${success === 1 ? 'employee' : 'employees'}`)
      }
    },
  })
}

export const useBulkAssignEmploymentCategory = () => {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: (data: BulkAssignEmploymentCategoryRequest) => bulkAssignEmploymentCategory(data),
    onSuccess: (result) => {
      queryClient.invalidateQueries({ queryKey: ['employees'] })
      const success = result.updatedCount
      const failed = result.failures?.length ?? 0
      if (failed > 0) {
        toast.warning(`Updated ${success} employees; ${failed} failed`)
      } else {
        toast.success(`Updated ${success} ${success === 1 ? 'employee' : 'employees'}`)
      }
    },
  })
}

export const useChangeEmployeeType = (employeeId: number) => {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: (data: ChangeEmployeeTypeRequest) => changeEmployeeType(employeeId, data),
    onSuccess: (_void, vars) => {
      queryClient.invalidateQueries({ queryKey: employeeKeys.all })
      queryClient.invalidateQueries({ queryKey: employeeKeys.profile(employeeId) })
      toast.success(`Type changed to ${vars.employeeType}`)
    },
  })
}

